import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Zap, Sparkles } from "lucide-react";
import { Button, Input, Textarea, Select, Checkbox, Card, CardContent, Modal, StatusBadge, LoadingBlock, EmptyState, toast, ConfirmDialog } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { PRICE_TYPES, priceLabel } from "./constants";
import { Field } from "../onboarding/_ui";

const EMPTY = {
  name: "", category: "General", customer_description: "", internal_description: "",
  price_type: "range", minimum_price: "", maximum_price: "", estimated_lead_value: "",
  duration_minutes: "60", directly_bookable: true, emergency_service: false, active: true, ai_price_disclosure: false,
};

export function ServicesPanel() {
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.svcList().then(setRows).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY }); setOpen(true); };
  const openEdit = (s: Record<string, unknown>) => {
    setEditing(s);
    setForm({
      name: (s.name as string) ?? "", category: (s.category as string) ?? "General",
      customer_description: (s.customer_description as string) ?? "", internal_description: (s.internal_description as string) ?? "",
      price_type: (s.price_type as string) ?? "range",
      minimum_price: s.minimum_price != null ? String(s.minimum_price) : "",
      maximum_price: s.maximum_price != null ? String(s.maximum_price) : "",
      estimated_lead_value: s.estimated_lead_value != null ? String(s.estimated_lead_value) : "",
      duration_minutes: String(s.duration_minutes ?? 60),
      directly_bookable: (s.directly_bookable as boolean) ?? true,
      emergency_service: (s.emergency_service as boolean) ?? false, active: (s.active as boolean) ?? true,
      ai_price_disclosure: (s.ai_price_disclosure as boolean) ?? false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (form.name.trim().length < 1) { toast.error("Service name is required."); return; }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(), category: form.category.trim() || "General",
        customer_description: form.customer_description.trim() || null,
        internal_description: form.internal_description.trim() || null,
        price_type: form.price_type, duration_minutes: Number(form.duration_minutes) || 60,
        directly_bookable: form.directly_bookable, emergency_service: form.emergency_service, active: form.active,
        ai_price_disclosure: form.ai_price_disclosure,
        minimum_price: form.minimum_price ? Number(form.minimum_price) : null,
        maximum_price: form.maximum_price ? Number(form.maximum_price) : null,
        estimated_lead_value: form.estimated_lead_value ? Number(form.estimated_lead_value) : null,
      };
      if (editing) await api.svcUpdate(editing.id as string, body);
      else await api.svcCreate(body);
      toast.success("Service saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try { await api.svcDelete(id); toast.success("Service deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const seedDemo = async () => {
    try { const r = await api.svcDemoSeed(); toast.success(`Loaded ${r.count} demo service(s)`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const showPrice = form.price_type !== "quote" && form.price_type !== "not_disclosed";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">What your business offers and how it's priced.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDemo} data-testid="svc-demo-seed"><Sparkles className="h-4 w-4" /> Load demo services</Button>
          <Button onClick={openNew} data-testid="svc-add"><Plus className="h-4 w-4" /> Add service</Button>
        </div>
      </div>

      {rows === null ? <LoadingBlock rows={3} /> : rows.length === 0 ? (
        <EmptyState icon={Zap} title="No services yet" description="Add the jobs your AI can discuss, quote and book."
          action={<Button onClick={openNew} data-testid="svc-empty-add"><Plus className="h-4 w-4" /> Add your first service</Button>} />
      ) : (
        <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="svc-list">
          {rows.map((s) => (
            <div key={s.id as string} className="flex items-center justify-between px-5 py-4" data-testid={`svc-item-${s.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-semibold text-slate-900">{s.name as string}</p>
                  {!(s.active as boolean) && <StatusBadge tone="slate">Inactive</StatusBadge>}
                  {(s.emergency_service as boolean) && <StatusBadge tone="red">Emergency</StatusBadge>}
                  {(s.directly_bookable as boolean) && <StatusBadge tone="blue">Bookable</StatusBadge>}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{s.category as string} · {priceLabel(s)} · {s.duration_minutes as number} min</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`svc-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setToDelete(s.id as string)} data-testid={`svc-delete-${s.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
              </div>
            </div>
          ))}
        </CardContent></Card>
      )}

      <Modal open={open} onOpenChange={setOpen} title={editing ? "Edit service" : "Add service"} description="Details your AI uses to quote and book this service." testid="svc-modal">
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" required testid="field-svc-name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="svc-name" /></Field>
            <Field label="Category" testid="field-svc-cat"><Input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="svc-category" /></Field>
          </div>
          <Field label="Customer description" testid="field-svc-cdesc"><Textarea value={form.customer_description} onChange={(e) => set("customer_description", e.target.value)} data-testid="svc-cdesc" /></Field>
          <Field label="Internal description" hint="Not shown to customers" testid="field-svc-idesc"><Textarea value={form.internal_description} onChange={(e) => set("internal_description", e.target.value)} data-testid="svc-idesc" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Price type" testid="field-svc-ptype"><Select value={form.price_type} onChange={(v) => set("price_type", v)} options={PRICE_TYPES} testid="svc-price-type" /></Field>
            <Field label="Duration (min)" testid="field-svc-dur"><Input type="number" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} data-testid="svc-duration" /></Field>
          </div>
          {showPrice && (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Min price ($)" testid="field-svc-min"><Input type="number" value={form.minimum_price} onChange={(e) => set("minimum_price", e.target.value)} data-testid="svc-min" /></Field>
              {form.price_type === "range" && <Field label="Max price ($)" testid="field-svc-max"><Input type="number" value={form.maximum_price} onChange={(e) => set("maximum_price", e.target.value)} data-testid="svc-max" /></Field>}
              <Field label="Est. lead value ($)" testid="field-svc-lead"><Input type="number" value={form.estimated_lead_value} onChange={(e) => set("estimated_lead_value", e.target.value)} data-testid="svc-lead" /></Field>
            </div>
          )}
          {showPrice && (
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="svc-ai-disclose-row">
              <Checkbox checked={form.ai_price_disclosure} onChange={(v) => set("ai_price_disclosure", v)} testid="svc-ai-disclose" />
              <span className="text-sm text-slate-700">Let the AI receptionist quote this price to customers
                <span className="block text-xs text-slate-400">Off by default — when off, the AI says pricing varies and offers to schedule instead of quoting a number.</span></span>
            </label>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3"><Checkbox checked={form.directly_bookable} onChange={(v) => set("directly_bookable", v)} testid="svc-bookable" /><span className="text-sm text-slate-700">Directly bookable</span></label>
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3"><Checkbox checked={form.emergency_service} onChange={(v) => set("emergency_service", v)} testid="svc-emergency" /><span className="text-sm text-slate-700">Emergency service</span></label>
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3"><Checkbox checked={form.active} onChange={(v) => set("active", v)} testid="svc-active" /><span className="text-sm text-slate-700">Active</span></label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="svc-save">{saving ? "Saving…" : "Save service"}</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Delete this service?"
        description="This permanently removes the service." confirmLabel="Delete" destructive
        onConfirm={() => { if (toDelete) remove(toDelete); setToDelete(null); }} testid="svc-delete-confirm" />
    </div>
  );
}
